import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "../components/sudanLanding.scss";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import IconManagement from "../assets/dev-actions.svg";
import IconFinancial from "../assets/cost-effective.svg";
import IconProject from "../assets/assessment-icon.svg";
import IconRelations from "../assets/world.svg";
import IconCompliance from "../assets/evidence-based.svg";
import IconRisk from "../assets/vital.svg";
import HeroVisual from "../assets/banner-capacity-accelerator.png";
import CertificateImg from "../assets/capacity-collaboration.jpg";
import { LanguageProvider, useLanguage } from "../i18n/LanguageContext";

const PROGRAM_STATS = [
   { num: "20", label: "Organizations selected per year" },
   { num: "4", label: "Weeks of guided facilitation" },
   { num: "Free", label: "No cost, no obligation" }
];

const WEEKS = [
   {
      week: "Week 1",
      title: "Know your organization",
      text: "Start with an honest picture of where your organization stands. Understand your strengths, identify the gaps that matter most to donors, and learn how to use KAPorg to track your progress throughout the program."
   },
   {
      week: "Week 2",
      title: "Manage money like a donor expects",
      text: "Build the financial systems, controls, and reporting practices that international donors require before they commit funding. Understand what audit readiness looks like in practice."
   },
   {
      week: "Week 3",
      title: "Win funding",
      text: "Learn how donors evaluate organizations, what makes a proposal stand out, and how to navigate the compliance requirements of UN agencies, bilateral donors, and pooled funds."
   },
   {
      week: "Week 4",
      title: "Deliver and prove it",
      text: "Complete your guided assessment with expert facilitation. Leave the program with a concrete, time-bound improvement plan and the documentation to support your next funding application."
   }
];

const PILLARS = [
   {
      title: "Organizational management",
      text: "Internal processes, human resources, governance structures, and operational efficiency.",
      icon: IconManagement
   },
   {
      title: "Financial management",
      text: "Budgeting, accounting systems, financial reporting, and audit readiness.",
      icon: IconFinancial
   },
   {
      title: "Project cycle management",
      text: "Planning, implementation, monitoring, evaluation, and learning across all programs.",
      icon: IconProject
   },
   {
      title: "External relations",
      text: "Stakeholder engagement, community accountability, communication, and advocacy.",
      icon: IconRelations
   },
   {
      title: "Donor compliance",
      text: "Alignment with major donor rules, regulations, and reporting requirements.",
      icon: IconCompliance
   },
   {
      title: "Risk, integrity & safeguarding",
      text: "Legal registration, anti-corruption, safeguarding, PSEA, whistleblower mechanisms, data protection, and counter-terrorism screening.",
      icon: IconRisk
   }
];

const ELIGIBILITY = [
   {
      title: "Registered for at least one year",
      text: "Your organization must be formally registered and in good standing for a minimum of one year."
   },
   {
      title: "Applied to a UN agency or bilateral donor at least three times",
      text: "Your organization has sought international funding and understands the requirements and barriers involved."
   },
   {
      title: "Successfully implemented at least one project",
      text: "Your organization has completed at least one project with an international organization and can provide references."
   },
   {
      title: "Actively using the KAPorg platform",
      text: "Your organization has registered on KAPorg and completed at least one self-assessment cycle before applying."
   }
];

const CapacityAcceleratorInner = () => {
   const { t, dir, lang } = useLanguage();

   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);

   return (
      <div className="App">
         <div
            className={`sudan-kaporg sudan-landing ${lang === "ar" ? "sudan-rtl" : ""}`}
            dir={dir}
         >
            <Navbar />

            {/* Hero */}
            <section className="sd-page-hero">
               <div className="container">
                  <div className="sd-page-hero-inner">
                     <div className="sd-page-hero-text">
                        <span className="sd-chip">{t("KAPorg Capacity Accelerator Program")}</span>
                        <h1>{t("The KAPorg Program: Build Capacity, Scale Impact.")}</h1>
                        <p>
                           {t(
                              "A free, four-week guided program for Sudanese NGOs and CSOs. Work with experts to assess your organization, build a concrete improvement plan, and strengthen the areas donors scrutinize most. Demonstrate your readiness with KAPorg."
                           )}
                        </p>
                        <div className="sd-page-hero-cta">
                           <Link to="/auth/registration" className="sd-btn sd-btn-accent">
                              {t("Apply for the 2026 program →")}
                           </Link>
                        </div>
                     </div>
                     <div className="sd-page-hero-visual">
                        <img src={HeroVisual} alt="" aria-hidden="true" />
                     </div>
                  </div>
               </div>
            </section>

            {/* Program stats — cards under the hero, mirroring the homepage */}
            <section className="sd-stats" style={{ padding: "72px 0 0", background: "#fff" }}>
               <div className="container">
                  <div className="sd-stats-grid cols-3" style={{ marginBottom: 0 }}>
                     {PROGRAM_STATS.map((stat) => (
                        <div className="sd-stat" key={stat.label}>
                           <div className="sd-stat-num">{t(stat.num)}</div>
                           <div className="sd-stat-label">{t(stat.label)}</div>
                        </div>
                     ))}
                  </div>
               </div>
            </section>

            {/* Section 1 - weekly learning journey + certificate */}
            <section className="sd-section sd-bg-white">
               <div className="container">
                  <div
                     className="sd-section-head"
                     style={{ textAlign: lang === "ar" ? "right" : "left", margin: "0 0 40px" }}
                  >
                     <h2 style={{ fontSize: "32px" }}>
                        {t("Four weeks of structured, expert-guided capacity development.")}
                     </h2>
                     <p>
                        {t(
                           "Each session builds directly on your self-assessment results, making the program specific to your organization's context and the communities you serve."
                        )}
                     </p>
                  </div>
                  <div className="sd-split stretch">
                     <div className="sd-split-main">
                        <div className="sd-timeline">
                           {WEEKS.map((item, index) => (
                              <div className="sd-timeline-item" key={item.week}>
                                 <div className="sd-timeline-marker">{index + 1}</div>
                                 <div className="sd-timeline-week">{t(item.week)}</div>
                                 <h4>{t(item.title)}</h4>
                                 <p>{t(item.text)}</p>
                              </div>
                           ))}
                        </div>
                     </div>
                     <div className="sd-split-aside">
                        <div className="sd-cert-card">
                           <div className="sd-cert-card-photo">
                              <img src={CertificateImg} alt="" aria-hidden="true" />
                           </div>
                           <div className="sd-cert-card-body">
                              <h4>{t("The KAPorg Certificate of Organizational Capacity")}</h4>
                              <p>
                                 {t(
                                    "Organizations that complete the program receive a certificate that demonstrates their internal initiative to build an organization capable of applying for and receiving funding, and implementing programs in the most transparent, accountable, and needs-driven ways."
                                 )}
                              </p>
                              <Link to="/auth/registration" className="sd-btn sd-btn-accent">
                                 {t("Apply for the 2026 program →")}
                              </Link>
                           </div>
                        </div>
                     </div>
                  </div>
               </div>
            </section>

            {/* Section 2 - six pillars */}
            <section className="sd-section sd-bg-light">
               <div className="container">
                  <div className="sd-section-head">
                     <span className="sd-eyebrow">{t("Assessment framework")}</span>
                     <h2>{t("Six Pillars of Organizational Capacity")}</h2>
                     <p>
                        {t(
                           "Our assessment framework is built on internationally recognized standards — directly aligned with the due diligence requirements of UN HACT, UNDP micro-assessments, EU PADOR, USAID pre-award surveys, and UK FCDO partner assessments."
                        )}
                     </p>
                  </div>
                  <div className="sd-card-grid cols-3">
                     {PILLARS.map((pillar) => (
                        <div className="sd-card" key={pillar.title}>
                           <img className="sd-card-icon" src={pillar.icon} alt="" aria-hidden="true" />
                           <h4>{t(pillar.title)}</h4>
                           <p>{t(pillar.text)}</p>
                        </div>
                     ))}
                  </div>
               </div>
            </section>

            {/* Section 3 - eligibility */}
            <section className="sd-section sd-bg-white">
               <div className="container">
                  <div className="sd-section-head">
                     <span className="sd-eyebrow">{t("Eligibility criteria")}</span>
                     <h2>{t("Who the program is for")}</h2>
                     <p>
                        {t(
                           "The program is designed for organizations that are already active and have a track record — and are ready to take the next step toward direct international funding."
                        )}
                     </p>
                  </div>
                  <div className="sd-card-grid cols-2">
                     {ELIGIBILITY.map((item) => (
                        <div className="sd-card" key={item.title}>
                           <h4>
                              <span className="sd-check">✓</span>
                              {t(item.title)}
                           </h4>
                           <p>{t(item.text)}</p>
                        </div>
                     ))}
                  </div>
               </div>
            </section>

            {/* Section 4 - closing */}
            <section className="sd-section sd-closing">
               <div className="container">
                  <div className="sd-closing-inner">
                     <h2>{t("Applications for 2026 are open.")}</h2>
                     <p>{t("Twenty organizations will be selected. Make sure yours is one of them.")}</p>
                     <div className="sd-closing-cta">
                        <Link to="/auth/registration" className="sd-btn sd-btn-accent">
                           {t("Apply now →")}
                        </Link>
                        <Link to="/auth/registration" className="sd-btn sd-btn-ghost-light">
                           {t("Register for free")}
                        </Link>
                     </div>
                  </div>
               </div>
            </section>

            <Footer />
         </div>
      </div>
   );
};

const CapacityAcceleratorPage = () => (
   <LanguageProvider>
      <CapacityAcceleratorInner />
   </LanguageProvider>
);

export default CapacityAcceleratorPage;
